import React, { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useGeneral } from '../../../contexts';
import styles from './GlobalLoader.module.css';

const GlobalLoader: React.FC = () => {
  const location = useLocation();
  const { loading, language } = useGeneral();
  const { getTranslation } = language;
  const [isNavigating, setIsNavigating] = useState(false);
  const prevLocationRef = useRef(location.pathname);
  const hideTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Track route changes
  useEffect(() => {
    if (location.pathname !== prevLocationRef.current) {
      // Clear any pending hide timer
      if (hideTimerRef.current) {
        clearTimeout(hideTimerRef.current);
        hideTimerRef.current = null;
      }
      setIsNavigating(true);
      prevLocationRef.current = location.pathname;
    }
  }, [location.pathname]);

  // Check if any loading state is active (excluding notification-related loading)
  const hasActiveLoading = Object.entries(loading).some(([key, value]) => {
    // Exclude notification-related loading states from triggering global loader
    if (key.startsWith('notifications.')) {
      return false;
    }
    return value === true;
  });

  // Hide loader when navigation completes and no loading states are active
  useEffect(() => {
    if (isNavigating && !hasActiveLoading) {
      // Small delay to ensure smooth transition and allow page to render
      hideTimerRef.current = setTimeout(() => {
        setIsNavigating(false);
        hideTimerRef.current = null;
      }, 200);
      return () => {
        if (hideTimerRef.current) {
          clearTimeout(hideTimerRef.current);
          hideTimerRef.current = null;
        }
      };
    }
  }, [isNavigating, hasActiveLoading]);

  // Show loader if navigating or if any loading state is active
  const showLoader = isNavigating || hasActiveLoading;

  if (!showLoader) {
    return null;
  }

  return (
    <div className={styles.globalLoader}>
      <div className={styles.loaderContent}>
        <div className={styles.spinner}></div>
        <p className={styles.loadingText}>{getTranslation('common.loading')}</p>
      </div>
    </div>
  );
};

export default GlobalLoader;

